import { Stack, TextField, Typography } from '@mui/material';
import {
  isValidLandingPageSlug,
  isValidLandingPageUrl,
  LANDING_PAGE_SLUG_MAX_LENGTH,
} from '@provision/landing-page-utils';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { FormSwitch } from 'src/components/Forms/FormSwitch/form-switch';
import { ColorPickerField } from 'src/pages/Settings/memberships/components/color-picker-field';
import { SegmentedToggleField } from 'src/pages/Settings/memberships/components/landing-page-tab/segmented-toggle-field';
import {
  CTA_MODE_OPTIONS,
  type LandingPageFormValues,
  LOGO_SIZE_OPTIONS,
} from 'src/pages/Settings/memberships/components/landing-page-tab/types';
import { panelFieldLabelSx } from 'src/pages/Settings/memberships/styles';
import { isValidColor } from 'src/utils/color-utils';

const CTA_NOTE =
  'Consultation links to your scheduling URL. Sign up sends clients into the activation flow; magic links log them in automatically.';
const COLORS_NOTE =
  'Colors are saved with the landing page config, not on the brand record. Leave a color empty to use your brand color.';

const COLOR_RULES = {
  validate: (value: string) =>
    !value.trim() || isValidColor(value.trim()) || 'Enter a valid color (#nnn or #nnnnnn)',
};

interface LandingPageSettingsTabProps {
  readOnly: boolean;
}

export const LandingPageSettingsTab = ({ readOnly }: LandingPageSettingsTabProps) => {
  const { control } = useFormContext<LandingPageFormValues>();
  const ctaMode = useWatch({ control, name: 'settings.ctaMode' });

  return (
    <Stack p={2} gap={3}>
      <Controller
        name="name"
        control={control}
        rules={{ required: 'Enter a page name.' }}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label="Page name"
            size="small"
            disabled={readOnly}
            error={Boolean(fieldState.error)}
            helperText={fieldState.error?.message ?? 'Shown to your team, not to visitors.'}
          />
        )}
      />

      <Controller
        name="slug"
        control={control}
        rules={{
          required: 'Enter a URL slug.',
          validate: (value) =>
            isValidLandingPageSlug(value) ||
            'Use lowercase letters, numbers and single hyphens, 2 to 48 characters.',
        }}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label="URL slug"
            size="small"
            disabled={readOnly}
            inputProps={{ maxLength: LANDING_PAGE_SLUG_MAX_LENGTH }}
            error={Boolean(fieldState.error)}
            helperText={
              fieldState.error?.message ?? 'The last part of the public page address.'
            }
          />
        )}
      />

      <Stack gap={1.5}>
        <SegmentedToggleField
          name="settings.ctaMode"
          label="Call to action"
          options={CTA_MODE_OPTIONS}
          readOnly={readOnly}
        />
        {ctaMode === 'consultation' ? (
          <Controller
            name="settings.consultationUrl"
            control={control}
            rules={{
              validate: (value) =>
                isValidLandingPageUrl(value.trim()) || 'Enter the scheduling URL (https://…).',
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Scheduling URL"
                size="small"
                disabled={readOnly}
                placeholder="https://calendly.com/your-team"
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
              />
            )}
          />
        ) : null}
        <Typography variant="body2" color="text.secondary">
          {CTA_NOTE}
        </Typography>
      </Stack>

      <FormSwitch
        type="switch"
        name="settings.showPricing"
        label="Show pricing"
        labelPlacement="end"
        disabled={readOnly}
        sx={{ m: 0, alignSelf: 'flex-start' }}
      />

      <SegmentedToggleField
        name="settings.logoSize"
        label="Logo size"
        options={LOGO_SIZE_OPTIONS}
        readOnly={readOnly}
      />

      <Stack gap={1}>
        <Typography variant="caption" sx={panelFieldLabelSx}>
          Brand color
        </Typography>
        <Stack sx={{ '& > *': { borderBottom: 1, borderColor: 'divider' } }}>
          <Controller
            name="settings.primaryColor"
            control={control}
            rules={COLOR_RULES}
            render={({ field, fieldState }) => (
              <ColorPickerField
                field={field}
                label="Primary"
                readOnly={readOnly}
                hasError={Boolean(fieldState.error)}
              />
            )}
          />
          <Controller
            name="settings.secondaryColor"
            control={control}
            rules={COLOR_RULES}
            render={({ field, fieldState }) => (
              <ColorPickerField
                field={field}
                label="Secondary"
                readOnly={readOnly}
                hasError={Boolean(fieldState.error)}
              />
            )}
          />
          <Controller
            name="settings.accentColor"
            control={control}
            rules={COLOR_RULES}
            render={({ field, fieldState }) => (
              <ColorPickerField
                field={field}
                label="Accent"
                readOnly={readOnly}
                hasError={Boolean(fieldState.error)}
              />
            )}
          />
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {COLORS_NOTE}
        </Typography>
      </Stack>
    </Stack>
  );
};
